import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Paper,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    TablePagination,
    InputAdornment
} from '@mui/material';
import { 
    Add as AddIcon, 
    Search as SearchIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';

import * as actions from '../redux.actions';
import * as selectors from '../redux.selectors';
import { COMPLEXITY_OPTIONS, ROWS_PER_PAGE_OPTIONS, DEPARTMENT_FILTER_ALL } from '../common';
import GoalTable from './GoalTable';
import GoalDialog from './GoalDialog';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import GoalUsageDialog from './GoalUsageDialog';

const GoalManagement = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Redux state
    const goals = useSelector(selectors.goalsSelector);
    const loading = useSelector(selectors.loadingSelector);
    const pagination = useSelector(selectors.paginationSelector);
    const filters = useSelector(selectors.filtersSelector);
    const sort = useSelector(selectors.sortSelector);
    const departments = useSelector(selectors.uniqueDepartmentsSelector);

    // Local state for search input (for immediate UI update)
    const [searchInput, setSearchInput] = useState('');
    const debounceTimerRef = useRef(null);

    // Sync search input with Redux filter on mount and when filter changes externally
    useEffect(() => {
        setSearchInput(filters.search);
    }, [filters.search]);

    // Initialize state from URL parameters on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        
        // Get URL parameters
        const urlSearch = params.get('search') || '';
        const urlComplexity = params.get('complexity') || '';
        const urlDepartmentId = params.get('departmentId') || '';
        const urlPage = parseInt(params.get('page'), 10) || 1;
        const urlLimit = parseInt(params.get('limit'), 10) || 10;
    const urlSortBy = params.get('sortBy') || 'updatedAt';
        const urlSortDescending = params.get('sortDescending') === 'true';
        
        // Set state from URL if parameters exist
        if (params.toString()) {
            dispatch(actions.setFilters({ 
                search: urlSearch, 
                complexity: urlComplexity, 
                departmentId: urlDepartmentId 
            }));
            dispatch(actions.setPagination({ 
                limit: urlLimit, 
                offset: urlPage 
            }));
            dispatch(actions.setSort({ 
                sortBy: urlSortBy, 
                sortDescending: urlSortDescending 
            }));
        }
        
        // Fetch data
        dispatch(actions.fetchGoals());
        dispatch(actions.fetchDepartments());
    }, [dispatch, location.search]); // Re-run if URL search changes

    // Update URL when state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const params = new URLSearchParams();
        
        // Add filters to URL
        if (filters.search) params.set('search', filters.search);
        if (filters.complexity) params.set('complexity', filters.complexity);
        if (filters.departmentId) params.set('departmentId', filters.departmentId);
        
        // Add pagination to URL
        if (pagination.offset !== 1) params.set('page', pagination.offset.toString());
        if (pagination.limit !== 10) params.set('limit', pagination.limit.toString());
        
        // Add sorting to URL
    if (sort.sortBy !== 'updatedAt') params.set('sortBy', sort.sortBy);
        if (sort.sortDescending) params.set('sortDescending', 'true');
        
        // Update URL without reloading the page
        const newSearch = params.toString();
        const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
        
        // Only update if URL actually changed
        if (location.search !== `?${newSearch}` && !(location.search === '' && newSearch === '')) {
            navigate(newUrl, { replace: true });
        }
    }, [filters, pagination, sort, navigate, location.pathname, location.search]);

    // Debounced search handler with minimum character requirement
    const handleSearchChange = useCallback((event) => {
        const newSearch = event.target.value;
        
        // Update UI immediately
        setSearchInput(newSearch);
        
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Set new timer
        debounceTimerRef.current = setTimeout(() => {
            // Only trigger API call if:
            // 1. Search is empty (clear search) OR
            // 2. Search has at least 2 characters
            if (newSearch === '' || newSearch.length >= 2) {
                dispatch(actions.setFilters({ search: newSearch }));
                if (pagination.offset !== 1) {
                    dispatch(actions.setPagination({ offset: 1 }));
                }
            }
        }, 500); // Wait 500ms after user stops typing
    }, [dispatch, pagination.offset]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Handlers
    const handleComplexityChange = (event) => {
        dispatch(actions.setFilters({ complexity: event.target.value }));
        if (pagination.offset !== 1) {
            dispatch(actions.setPagination({ offset: 1 })); // Reset to first page
        }
    };

    const handleDepartmentChange = (event) => {
        dispatch(actions.setFilters({ departmentId: event.target.value }));
        if (pagination.offset !== 1) {
            dispatch(actions.setPagination({ offset: 1 })); // Reset to first page
        }
    };

    const handlePageChange = (event, newPage) => {
        dispatch(actions.setPagination({ offset: newPage + 1 })); // MUI uses 0-based, API uses 1-based
    };

    const handleRowsPerPageChange = (event) => {
        dispatch(actions.setPagination({ 
            limit: parseInt(event.target.value, 10),
            offset: 1 // Reset to first page
        }));
    };

    const handleSort = (columnId) => {
        const isSameColumn = sort.sortBy === columnId;
        const newSortDescending = isSameColumn ? !sort.sortDescending : false;
        
        dispatch(actions.setSort({
            sortBy: columnId,
            sortDescending: newSortDescending
        }));
        if (pagination.offset !== 1) {
            dispatch(actions.setPagination({ offset: 1 })); // Reset to first page
        }
    };

    const handleCreate = () => {
        dispatch(actions.openGoalDialog());
    };

    const handleClearFilters = () => {
        setSearchInput(''); // Clear local search input
        dispatch(actions.setFilters({ search: '', complexity: '', departmentId: '' }));
    dispatch(actions.setSort({ sortBy: 'updatedAt', sortDescending: true }));
        dispatch(actions.setPagination({ limit: 10, offset: 1 }));
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: { xs: 'center', sm: 'left' } }}>
                    Goals
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    disabled={loading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Create Goal
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={(theme) => ({ p: 2, mb: 3, boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' })}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Search */}
                    <TextField
                        placeholder="Search by name or description (min 2 chars)..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ 
                            minWidth: { xs: '100%', sm: 250 }, 
                            flexGrow: 1
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                        helperText={searchInput.length > 0 && searchInput.length < 2 ? "Type at least 2 characters to search" : ""}
                    />

                    {/* Complexity Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Complexity</InputLabel>
                        <Select
                            value={filters.complexity}
                            label="Complexity"
                            onChange={handleComplexityChange}
                        >
                            {COMPLEXITY_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Department Filter */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={filters.departmentId}
                            label="Department"
                            onChange={handleDepartmentChange}
                        >
                            <MenuItem value="">{DEPARTMENT_FILTER_ALL.label}</MenuItem>
                            {departments.length === 0 && (
                                <MenuItem disabled>
                                    <em>Loading departments...</em>
                                </MenuItem>
                            )}
                            {departments.map((dept) => (
                                <MenuItem key={dept.id} value={dept.id}>
                                    {dept.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Clear Filters Button */}
                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ height: '40px', width: { xs: '100%', sm: 'auto' } }}
                    >
                        RESET
                    </Button>
                </Box>

                {/* Results count */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Total Goals: <strong>{pagination.total}</strong>
                    </Typography>
                </Box>
            </Paper>

            {/* Table */}
            <Paper sx={(theme) => ({ width: '100%', overflow: 'hidden', boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' })}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                
                {!loading && goals.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            No goals found. Create your first goal to get started.
                        </Typography>
                    </Box>
                )}

                {!loading && goals.length > 0 && (
                    <>
                        <GoalTable
                            goals={goals}
                            sort={sort}
                            onSort={handleSort}
                        />
                        
                        {/* Pagination */}
                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.offset - 1} // MUI uses 0-based, API uses 1-based
                            onPageChange={handlePageChange}
                            rowsPerPage={pagination.limit}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage="Rows per page:"
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                '& .MuiTablePagination-toolbar': {
                                    justifyContent: 'space-between',
                                    paddingLeft: 2,
                                    paddingRight: 2
                                },
                                '& .MuiTablePagination-spacer': {
                                    display: 'none'
                                },
                                '& .MuiTablePagination-selectLabel': {
                                    margin: 0
                                },
                                '& .MuiTablePagination-displayedRows': {
                                    margin: 0,
                                    marginLeft: 'auto'
                                }
                            }}
                        />
                    </>
                )}
            </Paper>

            {/* Dialogs */}
            <GoalDialog />
            <DeleteConfirmationDialog />
            <GoalUsageDialog />
        </Box>
    );
};

export default GoalManagement;
